/**
 * Aerocivil aviation radar — source inventory (Milestone A2 contract, not live).
 * @see docs/weather/aerocivil-radar-approval.md
 */
import {
  AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
  AEROCIVIL_AVIATION_RADAR_SOURCE_CODE,
  AEROCIVIL_AVIATION_RADAR_SOURCE_KEY,
} from "./aerocivilAviationRadar.js";

export const AEROCIVIL_AVIATION_RADAR_INVENTORY = {
  sourceKey: AEROCIVIL_AVIATION_RADAR_SOURCE_KEY,
  registrySourceCode: AEROCIVIL_AVIATION_RADAR_SOURCE_CODE,
  displayName: "Aerocivil Aviation Radar",
  agency: "Aerocivil — Meteorología Aeronáutica para Colombia",
  operator: "Unidad Administrativa Especial de Aeronáutica Civil de Colombia",
  sourceType: "aviation_radar_static_imagery",
  trustLevel: "official_aeronautical",
  trustLabel: "Official (aeronautical)",
  coverageArea: "Colombia aeronautical radar sites",
  accessStatus: "public_web_provisional",
  integrationStatus: "program_closure_ready",
  publicAccessStatus: "public_web_provisional",
  providerStatus: "feature_flagged_preview",
  expectedUpdateCadence: "5–10 min target (LEADS WxWeb operational cadence — confirm with Aerocivil)",
  expectedFormat:
    "LEADS WxWeb inline PNG (/image/inline/{id}) with optional GeoServer WMS backend",
  pipelineType: "inline_png_proxy",
  contactStatus: "outreach_pending",
  enabled: false,
  requiresPartnership: false,
  featureFlagEnv: "AEROCIVIL_RADAR_ENABLED",
  clientFeatureFlagEnv: "REACT_APP_AEROCIVIL_RADAR_ENABLED",
  configModule: "server/weather/config/aerocivilRadarApprovalContract.js",
  attribution: AEROCIVIL_AVIATION_RADAR_ATTRIBUTION,
  notes:
    "Milestone A10 complete: georef calibration, outreach tracking, program closure gates, and sign-off-check CLI. Enable production flags after operator sign-off + manual QA.",
  planDocPath: "docs/weather/aerocivil-radar-source-discovery.md",
  approvalDocPath: "docs/weather/aerocivil-radar-approval.md",
  releaseChecklistPath: "docs/weather/aerocivil-aviation-radar-release-checklist.md",
  productionRolloutPath: "docs/weather/aerocivil-production-rollout.md",
};

export const AEROCIVIL_AVIATION_OUTREACH_DRAFT_EN = `Subject: Data access inquiry — Aerocivil aeronautical radar for civic weather awareness (Midpoint Map)

Dear Aerocivil Meteorología Aeronáutica team,

We are building Midpoint Map, a community intelligence platform that helps residents understand weather conditions across Colombia. We integrate official weather sources with clear attribution, including IDEAM national radar, SIATA local sensors in Medellín, and labeled backup layers.

We would like to integrate Aerocivil aeronautical radar imagery as a separate, clearly labeled "Official (aeronautical)" layer — complementary to IDEAM and SIATA, not a replacement. Zoom Earth attributes Colombia radar to Aerocivil; we want residents to see the same upstream source with honest labeling in Midpoint Map.

Could you please advise:

1. Whether LEADS WxWeb inline radar imagery or GeoServer WMS layers may be cached and proxied server-side for public civic display
2. Approved attribution text (Spanish and English) for map legends and source transparency pages
3. Acceptable polling interval and daily request expectations for automated sync
4. Georeferencing metadata for inline PNG or WMS GetMap products (GTA, CRM mosaic, ADZ)
5. Whether a formal data agreement is required beyond public portal access
6. A technical contact for integration documentation and filename/URL stability

We will respect all access controls, use conservative polling, and never expose session credentials to browsers. We do not infer official emergency alerts from radar pixels.

Thank you for your time.

Best regards,
[Your name]
[Organization]
[Contact email]`;

export const AEROCIVIL_AVIATION_OUTREACH_DRAFT_ES = `Asunto: Consulta de acceso a datos — radar aeronáutico Aerocivil para información meteorológica cívica (Midpoint Map)

Estimado equipo de Meteorología Aeronáutica de Aerocivil:

Estamos desarrollando Midpoint Map, una plataforma de inteligencia comunitaria que ayuda a las personas a comprender las condiciones del tiempo en Colombia. Integramos fuentes oficiales con atribución clara, incluyendo el radar nacional IDEAM, sensores locales SIATA en Medellín y capas de respaldo debidamente etiquetadas.

Nos gustaría integrar las imágenes de radar aeronáutico de Aerocivil como una capa independiente, claramente identificada como "Oficial (aeronáutico)" — complementaria a IDEAM y SIATA, no como reemplazo. Zoom Earth atribuye el radar de Colombia a Aerocivil; queremos que las personas vean la misma fuente con etiquetado honesto en Midpoint Map.

¿Podrían indicarnos, por favor?

1. Si las imágenes inline del portal LEADS WxWeb o las capas GeoServer WMS pueden almacenarse en caché y servirse mediante proxy en el servidor para visualización cívica pública
2. El texto de atribución aprobado (español e inglés) para leyendas de mapa y páginas de transparencia de fuentes
3. El intervalo de consulta aceptable y las expectativas de solicitudes diarias para sincronización automatizada
4. Metadatos de georreferenciación para PNG inline o productos WMS GetMap (GTA, mosaico CRM, ADZ)
5. Si se requiere un convenio formal de datos además del acceso al portal público
6. Un contacto técnico para documentación de integración y estabilidad de URLs

Respetaremos todos los controles de acceso, usaremos sondeo conservador y nunca expondremos credenciales de sesión en el navegador. No inferimos alertas oficiales de emergencia a partir de píxeles de radar.

Muchas gracias por su tiempo.

Atentamente,
[Su nombre]
[Organización]
[Correo de contacto]`;

export function buildAerocivilAviationContactPayload() {
  return {
    subject: "Aerocivil aeronautical radar data access inquiry",
    english: AEROCIVIL_AVIATION_OUTREACH_DRAFT_EN,
    spanish: AEROCIVIL_AVIATION_OUTREACH_DRAFT_ES,
    inventory: AEROCIVIL_AVIATION_RADAR_INVENTORY,
    planDoc: AEROCIVIL_AVIATION_RADAR_INVENTORY.planDocPath,
    approvalDoc: AEROCIVIL_AVIATION_RADAR_INVENTORY.approvalDocPath,
  };
}
